import { useState, useEffect } from 'react';
import api from '../utils/api';

const QUEUE_KEY  = 'solvert_queue';
const MAX_ITEMS  = 5;

// ── Compresión de imágenes base64 ─────────────────────────────────────────────
function comprimirDataURL(dataURL, calidad) {
  return new Promise((resolve) => {
    if (!dataURL || !dataURL.startsWith('data:image')) { resolve(dataURL); return; }
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width  = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/jpeg', calidad));
      } catch {
        resolve(dataURL); // fallback si falla la compresión del canvas
      }
    };
    img.onerror = () => resolve(dataURL); // fallback: conserva original
    img.src = dataURL;
  });
}

async function comprimirPayload(payload) {
  const fotos = Array.isArray(payload.fotos) ? payload.fotos : [];

  const [fotosComprimidas, firmaTec, firmaCli] = await Promise.all([
    // Fotos: calidad 0.3 (mayor ahorro de espacio)
    Promise.all(fotos.map(async f => ({
      ...f,
      dataURL: await comprimirDataURL(f.dataURL, 0.3),
    }))),
    // Firmas: calidad 0.7 (mantiene legibilidad)
    comprimirDataURL(payload.firma_tecnico, 0.7),
    comprimirDataURL(payload.firma_cliente, 0.7),
  ]);

  return { ...payload, fotos: fotosComprimidas, firma_tecnico: firmaTec, firma_cliente: firmaCli };
}

// ── Estado global compartido entre todas las instancias del hook ──────────────
// Al cargar, resetea 'enviando' → 'pendiente' (el navegador pudo cerrarse a mitad)
let _cola = JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]').map(item =>
  item.estado === 'enviando' ? { ...item, estado: 'pendiente', error_msg: null } : item
);
let _enviando = false;
const _listeners = new Set();

function persistir() {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(_cola));
}

function notificar() {
  _listeners.forEach(fn => fn([..._cola]));
}

function actualizarItem(id, cambios) {
  _cola = _cola.map(i => i.id === id ? { ...i, ...cambios } : i);
  persistir();
  notificar();
}

function eliminarDeQueue(id) {
  _cola = _cola.filter(i => i.id !== id);
  persistir();
  notificar();
}

// ── Procesador de cola ────────────────────────────────────────────────────────
async function procesarCola() {
  if (_enviando || !navigator.onLine) return;

  const procesables = _cola.filter(i => i.estado === 'pendiente' || i.estado === 'error');
  if (procesables.length === 0) return;

  _enviando = true;

  for (const item of procesables) {
    if (!navigator.onLine) break;
    actualizarItem(item.id, { estado: 'enviando', error_msg: null });
    try {
      await api.post('/formularios', item.payload);
      eliminarDeQueue(item.id);
    } catch (err) {
      // Si es error de red, vuelve a pendiente para el próximo intento
      const esRed = !navigator.onLine || err.code === 'ERR_NETWORK' || !err.response;
      actualizarItem(item.id, {
        estado: esRed ? 'pendiente' : 'error',
        error_msg: esRed ? null : (err.response?.data?.error || 'Error del servidor'),
      });
    }
  }

  _enviando = false;
}

// ── Agregar ítem a la cola ────────────────────────────────────────────────────
// Retorna: 'ok' | 'duplicado' | 'cola_llena'
export async function agregarACola({ tipo, payload, ascensor }) {
  if (_cola.length >= MAX_ITEMS) return 'cola_llena';

  // Anti-duplicados: mismo ascensor + misma fecha + mismo tipo
  const duplicado = _cola.some(i =>
    i.payload?.ascensor_id === payload.ascensor_id &&
    i.payload?.fecha_visita === payload.fecha_visita &&
    i.payload?.tipo === payload.tipo
  );
  if (duplicado) return 'duplicado';

  // Comprimir imágenes antes de persistir para cuidar el espacio en localStorage
  const payloadComprimido = await comprimirPayload(payload);

  const item = {
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    tipo,
    payload: payloadComprimido,
    ascensor,
    fecha_creacion: new Date().toISOString(),
    estado: 'pendiente',
    error_msg: null,
  };

  _cola = [..._cola, item];
  persistir();
  notificar();
  return 'ok';
}

// ── Hook ──────────────────────────────────────────────────────────────────────
export function useOfflineQueue() {
  const [cola, setCola] = useState([..._cola]);

  useEffect(() => {
    _listeners.add(setCola);
    return () => _listeners.delete(setCola);
  }, []);

  useEffect(() => {
    // Intenta procesar al montar (por si quedaron pendientes de sesión anterior)
    if (navigator.onLine) procesarCola();

    const handleOnline = () => procesarCola();
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  return {
    cola,
    total:      cola.length,
    pendientes: cola.filter(i => i.estado === 'pendiente').length,
    errores:    cola.filter(i => i.estado === 'error').length,
    procesarCola,
    reintentar: (id) => {
      actualizarItem(id, { estado: 'pendiente', error_msg: null });
      procesarCola();
    },
    eliminar: (id) => eliminarDeQueue(id),
  };
}
